import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import './App.css';
import Dashboard from './views/Dashboard/components/Dashboard';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import AppContextProvider from './contexts/AppContextProvider';
import { createContext, ReactNode, useEffect, useState } from 'react';
import firebaseService from './firebase/firebaseService';
import firebase from 'firebase/compat/app';
import MainLayout from './MainLayout';
import KlydoScreen from './views/Klydos/components/KlydoScreen';
import Klydos from './views/Klydos/KlydosList/components/Klydos';
import CalendarEvents from './views/Calendar/components/CalendarEvents';
import PublicKlydoView from './views/PublicKlydo/components/PublicKlydoView';
import PublicCollectionView from './views/PublicCollection/components/PublicCollectionView';
import PublicCollectionKlydoView from './views/PublicCollection/components/PublicCollectionKlydoView';
import PublicCollectionCodeView from './views/PublicCollectionCode/components/PublicCollectionCodeView';
import TermsAndConditions from './views/TermsAndConditions/TermsAndConditions';
import * as Sentry from '@sentry/react';
import Help from './views/Help';
import Collections from './views/Collections/components/Collections';
import CreateCollectionContainer from './views/Collections/components/CreateCollection/CreateCollectionContainer';
import CollectionPreview from './views/Collections/components/CollectionPreview/CollectionPreview';
import GiftLayout from './views/Gift/GiftLayout';
import GiftHomePage from './views/Gift/GiftHomePage';
import GiftWizard from './views/Gift/GiftWizard';
import GiftDone from './views/Gift/GiftDone';
import GiftShare from './views/Gift/GiftShare';
import AlertContextProvider from './contexts/AlertContext';
import useLandingRedirect from './hooks/useLandingRedirect';
import useIdleRefresh from './hooks/useIdleRefresh';
import ArtistPage from './views/ArtistPage/components/ArtistPage';

const queryClient = new QueryClient();

interface AuthContextType {
  user: firebase.User | null;
  setUser: React.Dispatch<React.SetStateAction<firebase.User | null>>;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);
const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

const RoutesWrapper = ({ children }: { children: ReactNode }) => {
  const environment =
    !process.env.NODE_ENV || process.env.NODE_ENV === 'development'
      ? 'development'
      : 'production';
  if (environment === 'production') {
    return <SentryRoutes>{children}</SentryRoutes>;
  } else {
    return <Routes>{children}</Routes>;
  }
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<firebase.User | null>(null);

  useEffect(() => {
    const unsubscribe: firebase.Unsubscribe =
      firebaseService.onAuthStateChanged((authUser: firebase.User | null) => {
        setUser(authUser);
      });
    return () => unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};
function App() {
  useLandingRedirect();
  useIdleRefresh();

  return (
    <QueryClientProvider client={queryClient}>
      <AlertContextProvider>
        <BrowserRouter>
          <RoutesWrapper>
            {/* Public routes - outside of auth */}
            <Route
              path="/public/klydo/:klydoId"
              element={<PublicKlydoView />}
            />
            <Route
              path="/public/collection/:collectionId"
              element={<PublicCollectionView />}
            />
            <Route
              path="/public/collection/:collectionId/klydo/:klydoId"
              element={<PublicCollectionKlydoView />}
            />
            <Route
              path="/public/collection-code/:orderInfo"
              element={<PublicCollectionCodeView />}
            />
            <Route path="/terms" element={<TermsAndConditions />} />

            {/* Gift routes - public, no auth required */}
            <Route element={<GiftLayout />}>
              <Route path="/gift" element={<GiftHomePage />} />
              <Route path="/gift/wizard" element={<GiftWizard />} />
              <Route path="/gift/done" element={<GiftDone />} />
              <Route path="/gift/share" element={<GiftShare />} />
            </Route>

            {/* Protected routes - wrapped with auth */}
            <Route
              element={
                <AppContextProvider>
                  <MainLayout />
                </AppContextProvider>
              }
            >
              <Route path="/artist-page" element={<ArtistPage />} />
              <Route path="/klydo/:klydoId" element={<KlydoScreen />} />
              <Route path={'/dashboard'} element={<Dashboard />} />
              <Route path={'/klydos'} element={<Klydos />} />
              <Route path={'/calendar-events'} element={<CalendarEvents />} />
              <Route path={'/collections'} element={<Collections />} />
              <Route path={'/help'} element={<Help />} />
              <Route
                path="/collection/:collectionId"
                element={<CollectionPreview />}
              />
              <Route
                path="/collection/:collectionId/klydo/:klydoId"
                element={<KlydoScreen />}
              />
              <Route
                path="/collection/edit/:collectionId"
                element={<CreateCollectionContainer />}
              />
              <Route
                path="*"
                element={<Navigate replace to={'/dashboard'} />}
              />
            </Route>
          </RoutesWrapper>
        </BrowserRouter>
      </AlertContextProvider>
    </QueryClientProvider>
  );
}
export default App;
